import React, { useState, useEffect } from 'react'
import { Table, Spin, message, Layout, Button, Select, Input, Space, AutoComplete } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import { getOutstandingInvoices, triggerPayUsOrElse, updateInvoiceKey, updateExpectedByEOM } from 'domains/outstandingInvoices/api'
import styled from 'styled-components'

const { Content } = Layout

const StyledTable = styled(Table)`
  .ant-table-thead > tr > th:first-child,
  .ant-table-tbody > tr > td:first-child {
    position: sticky;
    left: 0;
    z-index: 1;
    background: #fff;
  }
  
  .ant-table-thead > tr > th:last-child,
  .ant-table-tbody > tr > td:last-child {
    position: sticky;
    right: 0;
    z-index: 1;
    background: #fff;
  }
  
  .ant-table-thead > tr > th:first-child {
    z-index: 2;
  }
  
  .ant-table-thead > tr > th:last-child {
    z-index: 2;
  }

  .ant-table-tbody > tr > td:not(.comments-cell) {
    height: 3.2em !important;
    max-height: 3.2em !important;
    line-height: 1.6 !important;
    overflow: hidden !important;
    word-wrap: break-word !important;
    white-space: normal !important;
    vertical-align: top !important;
    padding: 8px 16px !important;
  }

  .ant-table-tbody > tr > td:not(.comments-cell) > div {
    height: 3.2em !important;
    max-height: 3.2em !important;
    line-height: 1.6 !important;
    overflow: hidden !important;
    word-wrap: break-word !important;
    white-space: normal !important;
    display: -webkit-box !important;
    -webkit-line-clamp: 2 !important;
    -webkit-box-orient: vertical !important;
  }

  .ant-table-tbody > tr > td.comments-cell {
    height: 3.2em !important;
    max-height: 3.2em !important;
    line-height: 1.6 !important;
    vertical-align: top !important;
    padding: 8px 16px !important;
  }

  .comments-scrollable {
    height: 3.2em !important;
    max-height: 3.2em !important;
    overflow-y: auto !important;
    overflow-x: hidden !important;
    white-space: pre-wrap !important;
    word-break: break-word !important;
    line-height: 1.6 !important;
    padding: 0 !important;
    scrollbar-width: thin !important;
    scrollbar-color: #d9d9d9 transparent !important;
  }

  .comments-scrollable::-webkit-scrollbar {
    width: 6px;
  }

  .comments-scrollable::-webkit-scrollbar-track {
    background: transparent;
  }

  .comments-scrollable::-webkit-scrollbar-thumb {
    background-color: #d9d9d9;
    border-radius: 3px;
  }

  .comments-scrollable::-webkit-scrollbar-thumb:hover {
    background-color: #bfbfbf;
  }

  .ant-table-thead > tr > th {
    height: 3.2em !important;
    max-height: 3.2em !important;
    line-height: 1.6 !important;
    overflow: hidden !important;
    word-wrap: break-word !important;
    white-space: normal !important;
    vertical-align: top !important;
    padding: 8px 16px !important;
  }
`

const Wrapper = styled(Content)`
  padding: 20px;
  min-height: calc(100vh - 64px);
`

const OutstandingInvoices = () => {
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [searchText, setSearchText] = useState('')
  const [searchedColumn, setSearchedColumn] = useState('')

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    setLoading(true)
    try {
      const response = await getOutstandingInvoices()
      if (response && response.data && response.data.records) {
        setData(response.data.records)
      }
    } catch (error) {
      message.error('Failed to fetch outstanding invoices')
    } finally {
      setLoading(false)
    }
  }

  const handlePaginationChange = (page, size) => {
    setCurrentPage(page)
    setPageSize(size)
  }

  const handlePageSizeChange = (current, size) => {
    setCurrentPage(1) // Reset to first page when page size changes
    setPageSize(size)
  }

  const handleKeyUpdate = async (invoice, newKey) => {
    try {
      const response = await updateInvoiceKey(invoice, newKey)
      if (response && response.status === 200) {
        message.success('Key updated successfully!')
        fetchData()
      } else {
        message.error(`Error updating key: ${response ? JSON.stringify(response.data || response) : 'Unknown error'}`)
      }
    } catch (error) {
      message.error(`Error updating key: ${error.response ? JSON.stringify(error.response.data || error.response) : error.message || 'Unknown error'}`)
    }
  }

  const handleExpectedByEOMUpdate = async (invoice, newValue) => {
    if (newValue === undefined || newValue === null) {
      return
    }

    try {
      const response = await updateExpectedByEOM(invoice, newValue)
      if (response && response.status === 200) {
        message.success('Expected by EOM updated successfully!')
        fetchData()
      } else {
        message.error(`Error updating Expected by EOM: ${response ? JSON.stringify(response.data || response) : 'Unknown error'}`)
      }
    } catch (error) {
      message.error(`Error updating Expected by EOM: ${error.response ? JSON.stringify(error.response.data || error.response) : error.message || 'Unknown error'}`)
    }
  }

  const handlePayUsOrElse = async (record) => {
    try {
      const response = await triggerPayUsOrElse(record)
      if (response && response.status === 200) {
        message.success('Workflow success!')
      } else {
        message.error(`Error: ${response ? JSON.stringify(response.data || response) : 'Unknown error'}`)
      }
    } catch (error) {
      message.error(`Error: ${error.response ? JSON.stringify(error.response.data || error.response) : error.message || 'Unknown error'}`)
    }
  }

  const keyOptions = [
    { value: 'Wait', label: 'Wait' },
    { value: 'Paid', label: 'Paid' },
    { value: 'Paid to network', label: 'Paid to network' },
    { value: 'Nir', label: 'Nir' },
    { value: 'Sales', label: 'Sales' },
    { value: 'Scheduled', label: 'Scheduled' },
  ]

  const expectedByEOMOptions = [
    { value: 'Y', label: 'Y' },
    { value: '', label: '' },
  ]

  const getColumnSearchProps = (dataIndex, title) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${title || dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters, confirm, dataIndex)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
        : '',
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => {
          const searchInput = document.querySelector('.ant-table-filter-dropdown input')
          if (searchInput) searchInput.select()
        }, 100)
      }
    },
  })

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm()
    setSearchText(selectedKeys[0])
    setSearchedColumn(dataIndex)
  }

  const handleReset = (clearFilters, confirm, dataIndex) => {
    clearFilters()
    setSearchText('')
    setSearchedColumn('')
    confirm()
  }

  const getUniqueValues = (dataIndex) => {
    const uniqueValues = [...new Set(data.map(item => item[dataIndex]).filter(Boolean))]
    return uniqueValues.map(value => ({
      text: value,
      value: value,
    }))
  }

  const columns = [
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 180,
      fixed: 'left',
      ...getColumnSearchProps('description', 'Description'),
    },
    {
      title: 'Account No.',
      dataIndex: 'account_no',
      key: 'account_no',
      width: 140,
      ...getColumnSearchProps('account_no', 'Account No.'),
    },
    {
      title: 'Entity',
      dataIndex: 'entity',
      key: 'entity',
      width: 80,
      filters: data.length > 0 ? getUniqueValues('entity') : [],
      onFilter: (value, record) => record.entity === value,
    },
    {
      title: 'Pay Date',
      dataIndex: 'pay_date',
      key: 'pay_date',
      width: 120,
      ...getColumnSearchProps('pay_date', 'Pay Date'),
    },
    {
      title: 'Invoice',
      dataIndex: 'invoice',
      key: 'invoice',
      width: 150,
      ...getColumnSearchProps('invoice', 'Invoice'),
    },
    {
      title: 'Invoice Date',
      dataIndex: 'invoice_date',
      key: 'invoice_date',
      width: 150,
      ...getColumnSearchProps('invoice_date', 'Invoice Date'),
    },
    {
      title: 'Details',
      dataIndex: 'details',
      key: 'details',
      width: 250,
      ...getColumnSearchProps('details', 'Details'),
    },
    {
      title: 'Outstanding Sum',
      dataIndex: 'outstanding_sum',
      key: 'outstanding_sum',
      width: 180,
      render: (value) => `$${value}`,
      sorter: (a, b) => a.outstanding_sum - b.outstanding_sum,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
        const [minValue, maxValue] = selectedKeys[0] ? selectedKeys[0].split(',') : ['', '']
        return (
          <div style={{ padding: 8 }}>
            <div style={{ marginBottom: 8 }}>
              <Input
                placeholder="Min amount"
                value={minValue}
                onChange={(e) => {
                  const newMin = e.target.value
                  const newMax = maxValue
                  setSelectedKeys(newMin || newMax ? [`${newMin},${newMax}`] : [])
                }}
                onPressEnter={() => confirm()}
                style={{ width: '100%', marginBottom: 4 }}
                type="number"
              />
              <Input
                placeholder="Max amount"
                value={maxValue}
                onChange={(e) => {
                  const newMin = minValue
                  const newMax = e.target.value
                  setSelectedKeys(newMin || newMax ? [`${newMin},${newMax}`] : [])
                }}
                onPressEnter={() => confirm()}
                style={{ width: '100%' }}
                type="number"
              />
            </div>
            <Space>
              <Button
                type="primary"
                onClick={() => confirm()}
                icon={<SearchOutlined />}
                size="small"
                style={{ width: 90 }}
              >
                Filter
              </Button>
              <Button
                onClick={() => {
                  clearFilters()
                  confirm()
                }}
                size="small"
                style={{ width: 90 }}
              >
                Reset
              </Button>
            </Space>
          </div>
        )
      },
      onFilter: (value, record) => {
        const [minValue, maxValue] = value.split(',')
        const amount = parseFloat(record.outstanding_sum)
        const min = minValue ? parseFloat(minValue) : -Infinity
        const max = maxValue ? parseFloat(maxValue) : Infinity
        return amount >= min && amount <= max
      },
      filterIcon: (filtered) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
    },
    {
      title: 'Curr',
      dataIndex: 'currency',
      key: 'currency',
      width: 85,
      filters: data.length > 0 ? getUniqueValues('currency') : [],
      onFilter: (value, record) => record.currency === value,
    },
    {
      title: 'Late days',
      dataIndex: 'late_days',
      key: 'late_days',
      width: 150,
      sorter: (a, b) => a.late_days - b.late_days,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Min days"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Filter
            </Button>
            <Button
              onClick={() => {
                clearFilters()
                confirm()
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      onFilter: (value, record) => record.late_days >= parseInt(value || 0),
    },
    {
      title: 'Key',
      dataIndex: 'key',
      key: 'key',
      width: 150,
      filters: keyOptions.map(option => ({
        text: option.label,
        value: option.value,
      })),
      onFilter: (value, record) => record.key === value,
      render: (value, record) => (
        <Select
          value={value}
          options={keyOptions}
          onChange={(newKey) => handleKeyUpdate(record.invoice, newKey)}
          style={{ width: '100%' }}
          placeholder="Select key"
        />
      ),
    },
    {
      title: 'Expected by EOM',
      dataIndex: 'expected_by_eom',
      key: 'expected_by_eom',
      width: 180,
      render: (value, record) => (
        <Select
          value={value || ''}
          options={expectedByEOMOptions}
          onChange={(newValue) => {
            if (newValue !== (value || '%20')) {
              handleExpectedByEOMUpdate(record.invoice, newValue || '%20')
            }
          }}
          style={{ width: '100%' }}
          placeholder="Select Y or Empty"
          allowClear
        />
      ),
    },
    {
      title: 'Comments',
      dataIndex: 'comments',
      key: 'comments',
      width: 300,
      className: 'comments-cell',
      render: (text) => (
        <div className="comments-scrollable">
          {text}
        </div>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="primary"
          danger
          onClick={() => {
            handlePayUsOrElse(record)
          }}
        >
         Collect
        </Button>
      ),
    },
  ]

  return (
    <Wrapper>
      <h1>Outstanding Invoices</h1>
      {data.length > 0 && (
        <h4>Outstanding Sum: {`$${(data.reduce((acc, curr) => acc + curr.outstanding_sum, 0) || 0).toLocaleString()}`}</h4>
      )}
      <Spin spinning={loading}>
        <StyledTable
          columns={columns}
          dataSource={data}
          rowKey={(record, index) => record.invoice || index}
          scroll={{ x: 1500, y: '100%' }}
          pagination={{
            total: data.length,
            pageSize: pageSize,
            current: currentPage,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
            onChange: handlePaginationChange,
            onShowSizeChange: handlePageSizeChange,
          }}
        />
      </Spin>
    </Wrapper>
  )
}

export default OutstandingInvoices